import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UserSquare2 } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

const EMPTY = { name: "", email: "", phone: "", product: "", note: "" };

export default function ProductManagers({ embedded = false }) {
  const { isReadOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => setItems((await api.get("/product-managers")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEdit(it);
    setForm({
      name: it.name || "", email: it.email || "", phone: it.phone || "",
      product: it.product || "", note: it.note || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await api.patch(`/product-managers/${edit.id}`, form);
      else await api.post("/product-managers", form);
      toast.success("Tersimpan");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/product-managers/${id}`);
      toast.success("Product Manager dihapus");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      return false;
    }
  };
  const doConfirmDel = async () => {
    if (!confirmDel) return;
    if (await remove(confirmDel.id)) setConfirmDel(null);
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="product-managers-page">
      {!embedded && <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Product Manager</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">PIC Product Manager</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Daftar Product Manager (PIC) yang dapat dipilih pada setiap project.
          </p>
        </div>
        </div>}
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" onClick={openNew} disabled={isReadOnly} data-testid="new-pm-button"><Plus className="h-4 w-4 mr-2" /> PM Baru</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{edit ? "Edit PM" : "PM Baru"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="label-mini">Nama</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-name-input" />
              </div>
              <div>
                <Label className="label-mini">Product</Label>
                <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="mis. Firewall" data-testid="pm-product-input" />
              </div>
              <div>
                <Label className="label-mini">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pm-email-input" />
              </div>
              <div>
                <Label className="label-mini">Telp</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pm-phone-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="label-mini">Catatan</Label>
                <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pm-note-input" />
              </div>
              <Button type="submit" className="md:col-span-2 rounded-sm" data-testid="save-pm-button">Simpan</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-3">Nama</th>
              <th className="text-left px-6 py-3">Product</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Telp</th>
              <th className="text-left px-6 py-3">Catatan</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">
                <UserSquare2 className="inline h-4 w-4 mr-2" /> Belum ada Product Manager.
              </td></tr>
            )}
            {pageItems.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`pm-row-${it.id}`}>
                <td className="px-6 py-3 font-medium">{it.name}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.product || "-"}</td>
                <td className="px-6 py-3 mono text-xs">{it.email || "-"}</td>
                <td className="px-6 py-3 mono text-xs">{it.phone || "-"}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.note || "-"}</td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  {!isReadOnly && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-pm-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`delete-pm-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix="pm-pg" />
      </div>
      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.name}
        testIdPrefix="confirm-delete-pm"
      />
    </div>
  );
}
